import { FC } from "react";
import Chip, { type ChipOwnProps } from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";

// ── Profile order → base color mapping (matches PIQ) ──────────────────────

const PROFILE_COLORS: ChipOwnProps["color"][] = [
  "success", // Profile 1: green
  "primary", // Profile 2: blue
  "purple", // Profile 3: purple
];

/**
 * Returns the chip color based on score value and profile order.
 * Matches PIQ 3-tier logic:
 * - Score ≤ 49 (or null/undefined): "default" (gray)
 * - Score 50–79: profile base color (faded)
 * - Score 80+: profile base color (full)
 */
export function getChipColor(
  score: number | null | undefined,
  profileOrder: number
): ChipOwnProps["color"] {
  if (score == null || score <= 49) return "default";
  return PROFILE_COLORS[profileOrder - 1] ?? "default";
}

/** Returns opacity for a score chip (faded for 50-79, full for 80+). */
export function getChipOpacity(score: number | null | undefined): number {
  if (score == null || score <= 49) return 1;
  if (score <= 79) return 0.6;
  return 1;
}

/** Returns the base color for a profile by its 1-based order. */
export function getProfileColor(profileOrder: number): ChipOwnProps["color"] {
  return PROFILE_COLORS[profileOrder - 1] ?? "default";
}

// ── ScoreChip — displays a numeric score as a colored chip ────────────────

interface ScoreChipProps {
  score: number | null | undefined;
  color: ChipOwnProps["color"];
}

export const ScoreChip: FC<ScoreChipProps> = ({ score, color }) => {
  if (score == null) {
    return (
      <Chip
        label="?"
        size="small"
        color="default"
        sx={{ borderRadius: 999, minWidth: 40 }}
      />
    );
  }
  return (
    <Chip
      label={Math.round(score)}
      size="small"
      color={color}
      sx={{ borderRadius: 999, minWidth: 40, opacity: getChipOpacity(score) }}
    />
  );
};

// ── ProfileScoreChip — ScoreChip with profile name tooltip ────────────────

interface ProfileScoreChipProps {
  score: number | null | undefined;
  profileName: string;
  profileOrder: number;
}

export const ProfileScoreChip: FC<ProfileScoreChipProps> = ({
  score,
  profileName,
  profileOrder,
}) => {
  const color = getChipColor(score, profileOrder);
  const label = score != null ? Math.round(score) : "—";
  return (
    <Tooltip title={profileName} placement="top">
      <span role="img" aria-label={`${profileName}: ${label}`}>
        <ScoreChip score={score} color={color} />
      </span>
    </Tooltip>
  );
};
